import { formatPhone, type LngLat, type MapPoint, type MapTruck, ZoneMap } from "@majitap/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useCompanyFormat } from "../../company";
import { type DeliveryRecord, etaText, isLive } from "../../deliveries";
import { ageOf, decodePolyline, trailQuery, truckState } from "../../tracking";
import page from "../orders/record.module.css";
import styles from "./delivery.module.css";

type Point = { latitude: number | null; longitude: number | null } | null | undefined;
const lngLatOf = (p: Point): LngLat | null =>
  p && p.latitude !== null && p.longitude !== null ? [p.longitude, p.latitude] : null;

/** The route (`Console / Delivery`): from the fill site to the customer, when both are on the map. */
/** Where the truck has been since the driver accepted, at most the last 24 hours (the trail's window). */
function trailSince(d: DeliveryRecord) {
  const accepted = d.events.find((e) => e.to_status === "accepted")?.occurred_at;
  const floor = Date.now() - 23 * 3_600_000;
  return new Date(Math.max(accepted ? Date.parse(accepted) : floor, floor)).toISOString();
}

export function RouteSection({ delivery: d }: { delivery: DeliveryRecord }) {
  const { timezone } = useCompanyFormat();
  const eta = d.eta ? etaText(d.eta, timezone) : null;
  const fillsite = d.assignment?.fillsite ?? null;
  const from = lngLatOf(fillsite);
  const to = lngLatOf(d.location);
  // the truck live while it is on this job, with its trail since it was accepted (P8-51)
  const position = d.truck_position;
  const truckId = d.assignment?.truck?.id;
  const live = Boolean(position && truckId && isLive(d.status));
  const [since] = useState(() => trailSince(d));
  const trail = useQuery({
    ...trailQuery(truckId ?? "", since),
    enabled: live,
    refetchInterval: live ? 10_000 : false,
  });
  // once delivered, the path the driver took from acceptance to delivery, kept on the delivery
  const path = d.status === "delivered" && d.path_polyline ? decodePolyline(d.path_polyline) : null;
  const trucks: MapTruck[] =
    position && truckId
      ? [
          {
            id: truckId,
            lng: position.longitude,
            lat: position.latitude,
            state: truckState({ stale: position.stale, speed_mps: position.speed_mps, delivery: null }),
            label: d.assignment?.truck?.plate_number,
            title: `${d.assignment?.truck?.plate_number ?? "The truck"}, ${d.eta ? `${d.eta.minutes} min to go, ` : ""}seen ${ageOf(position.recorded_at)} ago`,
          },
        ]
      : [];
  const here: LngLat | null = position ? [position.longitude, position.latitude] : null;
  const points: MapPoint[] =
    from && fillsite ? [{ id: fillsite.id, lng: from[0], lat: from[1], label: fillsite.name }] : [];
  const directions = to
    ? `https://www.google.com/maps/dir/?${new URLSearchParams({
        api: "1",
        destination: `${to[1]},${to[0]}`,
        ...(from ? { origin: `${from[1]},${from[0]}` } : {}),
      })}`
    : null;
  return (
    <section className={page.section} aria-label="Route">
      <div className={page.sectionHead}>
        <div>
          <h2 className={page.sectionTitle}>Route</h2>
          <p className={page.small}>
            {eta
              ? eta.line
              : path
                ? "The path the driver took, from acceptance to delivery"
                : from
                  ? "From the fill site to the customer"
                  : "Where it goes"}
          </p>
        </div>
        {directions ? (
          <a href={directions} target="_blank" rel="noreferrer" className={page.link}>
            Open in Maps
          </a>
        ) : null}
      </div>
      {to ? (
        <div className={page.panel}>
          <ZoneMap
            label={`The route of ${d.delivery_number}`}
            height="18rem"
            points={points}
            pointsName="Fill site"
            pin={{
              lng: to[0],
              lat: to[1],
              label: d.location?.label ?? "The customer",
              note: d.location?.address_line ?? undefined,
            }}
            route={from ? [from, to] : null}
            trucks={trucks}
            trail={live && trail.data ? trail.data.points.map((p): LngLat => [p.lng, p.lat]) : path}
            focus={[...(from ? [from] : []), to, ...(here ? [here] : []), ...(path ?? [])]}
          />
        </div>
      ) : (
        <p className={page.muted}>The place has no point on the map.</p>
      )}
    </section>
  );
}

/** Where the truck is now, from the driver's phone (P1-22): km to go, speed, how fresh. */
function Whereabouts({ delivery: d }: { delivery: DeliveryRecord }) {
  const p = d.truck_position;
  if (!p) return null;
  const line = p.stale
    ? `Not seen for ${ageOf(p.recorded_at)}: the driver's phone may be off or offline.`
    : [
        d.km_to_go === null ? null : `${d.km_to_go} km to go`,
        // number-ok: a speed in km/h, not money
        p.speed_mps === null ? null : `${Math.round(p.speed_mps * 3.6)} km/h`,
        `seen ${ageOf(p.recorded_at)} ago`,
      ]
        .filter(Boolean)
        .join(" · ");
  return <p className={p.stale ? styles.note : page.small}>Where it is: {line}</p>;
}

function Tile({ label, title, sub }: { label: string; title: ReactNode; sub?: ReactNode }) {
  return (
    <div className={styles.tile}>
      <span className={styles.small}>{label}</span>
      <strong>{title}</strong>
      {sub ? <span className={styles.small}>{sub}</span> : null}
    </div>
  );
}

/** Truck and crew: who carries it, in what, filled where. Empty until it's planned. */
export function CrewSection({ delivery: d }: { delivery: DeliveryRecord }) {
  const a = d.assignment;
  const truck = a?.truck;
  return (
    <section className={page.section} aria-label="Truck and crew">
      <div>
        <h2 className={page.sectionTitle}>Truck and crew</h2>
        <p className={page.small}>
          {a?.assigned_by ? `Planned by ${a.assigned_by.name}` : a ? "Planned" : "Not planned yet: assign a driver"}
        </p>
      </div>
      {a ? (
        <>
          <div className={styles.tiles}>
            <Tile
              label="Truck"
              title={truck?.plate_number ?? "No truck"}
              sub={
                truck
                  ? [
                      [truck.make, truck.model].filter(Boolean).join(" "),
                      `${truck.capacity_litres.toLocaleString("en-KE")} L`,
                      truck.fleet_number !== null ? `fleet no. ${truck.fleet_number}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : null
              }
            />
            <Tile
              label="Driver"
              title={a.driver?.name ?? "No driver"}
              sub={a.driver ? formatPhone(a.driver.phone_number) || "No phone" : null}
            />
            <Tile
              label="Assistant"
              title={a.assistant?.name ?? "None"}
              sub={a.assistant?.phone ? formatPhone(a.assistant.phone) : null}
            />
            <Tile
              label="Fill site"
              title={a.fillsite?.name ?? "None: a parcel"}
              sub={
                a.fillsite
                  ? [a.fillsite.address_line, a.fillsite.partner ? `${a.fillsite.partner.name}'s` : null]
                      .filter(Boolean)
                      .join(" · ")
                  : null
              }
            />
          </div>
          <Whereabouts delivery={d} />
          {truck?.partner ? (
            <p className={styles.note}>
              The truck is {truck.partner.name}'s: they're paid this delivery's haulage once it's delivered.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
